/**
 * Редактор стилей заголовка для блока типа "heading".
 */

import React, { FC } from 'react';
import { Slider } from 'antd';

// ==================== ТИПЫ ====================

export interface HeadingStyles {
    fontSize: number;
    fontWeight: number;
    color: string;
}

export interface HeadingStyleEditorProps {
    /** Текущие стили */
    styles: HeadingStyles;
    /** Обработчик изменения стилей */
    onStylesChange: (styles: HeadingStyles) => void;
    /** Текст для предпросмотра */
    previewText?: string;
}

// ==================== КОМПОНЕНТ ====================

export const HeadingStyleEditor: FC<HeadingStyleEditorProps> = ({
    styles,
    onStylesChange,
    previewText = 'Введите текст заголовка',
}) => {
    const handleFontSizeChange = (value: number) => {
        onStylesChange({ ...styles, fontSize: value });
    };
    
    const handleFontWeightChange = (value: number) => {
        onStylesChange({ ...styles, fontWeight: value });
    };
    
    const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onStylesChange({ ...styles, color: e.target.value });
    };
    
    return (
        <div style={{ 
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px',
            marginBottom: '20px',
        }}>
            {/* Левая колонка - настройки */}
            <div style={{ 
                padding: '20px',
                background: '#fafafa',
                borderRadius: '12px',
                border: '1px solid #e8e8e8',
            }}>
                <div style={{ 
                    fontWeight: 600, 
                    fontSize: '14px', 
                    marginBottom: '20px',
                    color: '#333',
                }}>
                    Настройки стиля
                </div>
                
                {/* Размер шрифта */}
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                        Размер шрифта: {styles.fontSize}px
                    </div>
                    <Slider
                        min={12}
                        max={72}
                        value={styles.fontSize}
                        onChange={handleFontSizeChange}
                    />
                </div>
                
                {/* Жирность */}
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                        Жирность: {styles.fontWeight}
                    </div>
                    <Slider
                        min={100}
                        max={900}
                        step={100}
                        value={styles.fontWeight}
                        onChange={handleFontWeightChange}
                    />
                </div>
                
                {/* Цвет */}
                <div>
                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                        Цвет текста
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <input 
                            type="color" 
                            value={styles.color}
                            onChange={handleColorChange}
                            style={{ 
                                width: '48px', 
                                height: '36px', 
                                border: '1px solid #d9d9d9', 
                                borderRadius: '6px', 
                                cursor: 'pointer',
                                padding: '2px',
                            }}
                        />
                        <span style={{ 
                            fontFamily: 'monospace',
                            fontSize: '13px',
                            color: '#666',
                            background: 'white',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: '1px solid #d9d9d9',
                        }}>
                            {styles.color}
                        </span>
                    </div>
                </div>
            </div>
            
            {/* Правая колонка - предпросмотр */}
            <div style={{ 
                padding: '20px',
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #e8e8e8',
                display: 'flex',
                flexDirection: 'column',
            }}>
                <div style={{ 
                    fontSize: '12px', 
                    color: '#999',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '16px',
                }}>
                    Предпросмотр
                </div>
                <div style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    padding: '20px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    minHeight: '80px',
                }}>
                    <div style={{
                        fontSize: `${styles.fontSize}px`,
                        fontWeight: styles.fontWeight,
                        color: styles.color,
                        lineHeight: 1.3,
                        wordBreak: 'break-word',
                    }}>
                        {previewText}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HeadingStyleEditor;
